import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { uploadPhoto, photoSrc } from "@/lib/uploads";
import { FileText, Camera, ChevronRight, Sparkles, Zap, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PhotoUploader from "@/components/PhotoUploader";
import MasterSelect from "@/components/MasterSelect";
import { CLAIMS } from "@/constants/testIds";
import { toast } from "sonner";

const ISSUES = [
  { id: "screen", label: "Cracked screen" },
  { id: "battery", label: "Battery failure" },
  { id: "water", label: "Water damage" },
  { id: "audio", label: "Speaker / microphone" },
  { id: "camera", label: "Camera fault" },
  { id: "charging", label: "Charging / port issue" },
  { id: "keyboard", label: "Keyboard / trackpad" },
  { id: "hinge", label: "Hinge / physical damage" },
  { id: "other", label: "Other" },
];

function fileToDataUrl(_file) { return Promise.resolve(""); }  // legacy shim

const STEPS = [
  { id: "device", label: "Device" },
  { id: "issue", label: "Issue" },
  { id: "photos", label: "Photos" },
  { id: "review", label: "Review" },
];

export default function ClaimNew() {
  const nav = useNavigate();
  const loc = useLocation();
  const [devices, setDevices] = useState([]);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    device_id: loc.state?.deviceId || "",
    issue_category: "",
    description: "",
    photos: [],
    police_report_file_id: "",
    police_reference: "",
    incident_date: "",
  });
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const loadDevices = useCallback(async () => {
    const { data } = await api.get("/devices");
    setDevices(data);
  }, []);
  useEffect(() => { loadDevices(); }, [loadDevices]);

  const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/claims", form);
      toast.success("Claim submitted");
      nav(`/app/claims/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (form.photos.length === 0) { toast.error("Add at least one photo first"); return; }
    setPreviewing(true);
    setPreview(null);
    try {
      const { data } = await api.post("/claims/preview-damage", {
        photos: form.photos,
        description: form.description,
        issue_category: form.issue_category,
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const canNext =
    (step === 0 && !!form.device_id) ||
    (step === 1 && !!form.issue_category && form.description.length >= 10) ||
    (step === 2) ||
    step === 3;

  const selectedDevice = devices.find(d => d.id === form.device_id);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <div className="overline text-secondary">Claim wizard</div>
        <h1 className="mt-1 font-display text-4xl font-light tracking-tight">File a new claim.</h1>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <div className={`flex items-center gap-2 ${i <= step ? "text-primary" : "text-muted-foreground"}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${i < step ? "bg-primary text-primary-foreground" : i === step ? "border-2 border-primary text-primary" : "border border-border"}`}>
                {i + 1}
              </div>
              <span className="text-xs hidden sm:inline">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-primary" : "bg-border"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 lg:p-8 min-h-[380px] animate-fade-up">
        {step === 0 && (
          <>
            <h2 className="font-display text-2xl">Which device is affected?</h2>
            <p className="text-sm text-muted-foreground mt-1">Pick from your registered phones.</p>
            <div className="mt-6">
              <Label className="text-xs">Device</Label>
              <Select value={form.device_id} onValueChange={v => setForm({...form, device_id: v})}>
                <SelectTrigger data-testid={CLAIMS.deviceSelect} className="mt-1.5"><SelectValue placeholder="Choose a device" /></SelectTrigger>
                <SelectContent>
                  {devices.map(d => <SelectItem key={d.id} value={d.id}>{d.brand} {d.model} · ••{d.imei.slice(-4)}</SelectItem>)}
                </SelectContent>
              </Select>
              {devices.length === 0 && (
                <div className="mt-4 text-sm text-muted-foreground">
                  You haven't registered any device yet. <Link to="/app/devices" className="text-primary underline">Register one</Link>.
                </div>
              )}
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <h2 className="font-display text-2xl">What happened?</h2>
            <p className="text-sm text-muted-foreground mt-1">Choose the issue category and describe it briefly.</p>
            <div className="mt-3 bg-primary/5 border border-primary/20 rounded-lg p-3 text-xs text-primary flex items-start gap-2" data-testid="claim-coverage-note">
              <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Submit any issue — if it isn't fully covered by your plan, our team will review your case and get back to you within 24 hours.
              </span>
            </div>
            <div className="mt-6 space-y-4">
              <div>
                <Label className="text-xs">Issue category</Label>
                <Select
                  value={form.issue_category}
                  onValueChange={(v) => setForm({ ...form, issue_category: v })}
                >
                  <SelectTrigger data-testid={CLAIMS.issueSelect} className="mt-1.5">
                    <SelectValue placeholder="Select an issue" />
                  </SelectTrigger>
                  <SelectContent>
                    {ISSUES.map((i) => (
                      <SelectItem key={i.id} value={i.id} data-testid={`claim-issue-${i.id}`}>
                        {i.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea
                  data-testid={CLAIMS.description}
                  value={form.description}
                  onChange={e => setForm({...form, description: e.target.value})}
                  placeholder="What happened? When did it happen? Any relevant context?"
                  rows={5}
                  className="mt-1.5"
                />
                <div className="text-[10px] text-muted-foreground mt-1">{form.description.length}/500 (min 10)</div>
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="font-display text-2xl">Upload photos</h2>
            <p className="text-sm text-muted-foreground mt-1">Up to 4 photos. Drag &amp; drop, browse, or snap directly with your camera.</p>
                <div className="mt-6">
                  <PhotoUploader
                    value={form.photos}
                    onChange={(v) => setForm({ ...form, photos: v })}
                    purpose="claim"
                    multiple
                    max={4}
                    testIdPrefix="claim-photo"
                  />
                </div>
                <div className="mt-4 text-xs text-muted-foreground flex items-start gap-2">
                  <Sparkles className="w-3 h-3 text-secondary mt-0.5 shrink-0" />
                  Photos are analysed by Gemini for damage severity, an estimated repair cost, and fraud detection.
                </div>
                {form.photos.length > 0 && (
                  <div className="mt-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={runPreview}
                      disabled={previewing}
                      data-testid="ai-preview-btn"
                    >
                      {previewing ? (
                        <>Analysing…</>
                      ) : (
                        <><Zap className="w-3.5 h-3.5 mr-1.5" /> AI pre-check</>
                      )}
                    </Button>
                    <span className="ml-2 text-xs text-muted-foreground">See severity + repair estimate before you submit.</span>
                  </div>
                )}
                {preview && (
                  <div
                    data-testid="ai-preview-result"
                    className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="overline text-secondary flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> AI pre-check
                        </div>
                        <div className="mt-2 flex items-baseline gap-3">
                          <span className="font-display text-2xl capitalize" data-testid="ai-preview-severity">{preview.severity}</span>
                          <span className="text-sm text-foreground/70">damage</span>
                        </div>
                        <div className="mt-1 text-sm text-foreground/80" data-testid="ai-preview-summary">{preview.summary}</div>
                      </div>
                      <div className="text-right">
                        <div className="overline text-muted-foreground text-[10px]">Est. repair</div>
                        <div className="font-display text-xl" data-testid="ai-preview-estimate">£{preview.estimate_gbp?.toFixed(2)}</div>
                        <div className="text-[10px] text-muted-foreground">confidence {Math.round((preview.confidence || 0) * 100)}%</div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
                      {preview.covered_by_plan === true && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-1">
                          <CheckCircle2 className="w-3 h-3" /> Covered by your plan
                        </span>
                      )}
                      {preview.covered_by_plan === false && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 text-destructive px-2 py-1">
                          <AlertTriangle className="w-3 h-3" /> Not covered — you may pay full repair cost
                        </span>
                      )}
                      {preview.fraud_flag && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-secondary/20 text-secondary px-2 py-1">
                          <AlertTriangle className="w-3 h-3" /> Photo flagged for manual review
                        </span>
                      )}
                    </div>
                  </div>
                )}
          </>
        )}

        {step === 3 && (
          <>
            <h2 className="font-display text-2xl">Review & submit</h2>
            <p className="text-sm text-muted-foreground mt-1">Confirm details, then we'll validate your claim.</p>
            <div className="mt-6 space-y-4">
              <div className="p-4 bg-muted/60 rounded-xl">
                <div className="overline text-muted-foreground">Device</div>
                <div className="mt-1">{selectedDevice ? `${selectedDevice.brand} ${selectedDevice.model}` : "—"}</div>
              </div>
              <div className="p-4 bg-muted/60 rounded-xl">
                <div className="overline text-muted-foreground">Issue</div>
                <div className="mt-1 capitalize">{form.issue_category || "—"}</div>
                <div className="mt-2 text-sm text-foreground/70">{form.description}</div>
              </div>
              {form.photos.length > 0 && (
                <div className="p-4 bg-muted/60 rounded-xl">
                  <div className="overline text-muted-foreground">Photos</div>
                  <div className="mt-2 flex gap-2">
                    {form.photos.map((p, i) => <img key={`${p.slice(0, 48)}-${i}`} src={photoSrc(p)} alt="" className="w-16 h-16 rounded object-cover" />)}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button variant="ghost" onClick={back} disabled={step === 0} data-testid={CLAIMS.back}>Back</Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={next} disabled={!canNext} data-testid={CLAIMS.next}>Continue <ChevronRight className="w-4 h-4 ml-1" /></Button>
        ) : (
          <Button onClick={submit} disabled={saving} data-testid={CLAIMS.submit} className="bg-primary text-primary-foreground">
            {saving ? "Analysing damage…" : "Submit claim"}
          </Button>
        )}
      </div>
    </div>
  );
}
